import React, { useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Grid,
} from "@mui/material";
import DocumentCard from "./DocumentCard";

type DocumentCategory =
  | "contract"
  | "invoice"
  | "permit"
  | "receipt"
  | "warranty"
  | "other";

interface Document {
  id: string;
  file_name: string;
  file_url: string;
  signed_url: string | null;
  category: DocumentCategory;
  upload_date: string;
  uploaded_by: string;
}

interface DocumentsListProps {
  documents: Document[];
  loading: boolean;
  error: string | null;
  currentUserId: string;
  onDelete: (documentId: string) => Promise<void>;
}

const categoryLabels: Record<DocumentCategory, string> = {
  contract: "Contract",
  invoice: "Invoice",
  permit: "Permit",
  receipt: "Receipt",
  warranty: "Warranty",
  other: "Other",
};

const DocumentsList: React.FC<DocumentsListProps> = ({
  documents,
  loading,
  error,
  currentUserId,
  onDelete,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<
    DocumentCategory | "all"
  >("all");

  const filteredDocuments =
    selectedCategory === "all"
      ? documents
      : documents.filter((doc) => doc.category === selectedCategory);

  const sortedDocuments = [...filteredDocuments].sort(
    (a, b) =>
      new Date(b.upload_date).getTime() - new Date(a.upload_date).getTime(),
  );

  const handleDelete = async (documentId: string) => {
    try {
      await onDelete(documentId);
    } catch (error) {
      console.error("Failed to delete document:", error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Stack spacing={3}>
      {/* Filter */}
      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Category</InputLabel>
          <Select
            value={selectedCategory}
            onChange={(e) =>
              setSelectedCategory(e.target.value as DocumentCategory | "all")
            }
            label="Filter by Category"
          >
            <MenuItem value="all">All Documents</MenuItem>
            {(Object.keys(categoryLabels) as DocumentCategory[]).map((cat) => (
              <MenuItem key={cat} value={cat}>
                {categoryLabels[cat]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          {sortedDocuments.length} document
          {sortedDocuments.length !== 1 ? "s" : ""}
        </Typography>
      </Box>

      {/* Empty State */}
      {sortedDocuments.length === 0 && (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No documents found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedCategory !== "all"
              ? `Upload a ${categoryLabels[selectedCategory as DocumentCategory]} document to get started.`
              : "Upload documents to organize your project files."}
          </Typography>
        </Box>
      )}

      {/* Documents Grid */}
      {sortedDocuments.length > 0 && (
        <Grid container spacing={2}>
          {sortedDocuments.map((document) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={document.id}>
              <DocumentCard
                document={document}
                canDelete={document.uploaded_by === currentUserId}
                onDelete={handleDelete}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Stack>
  );
};

export default DocumentsList;
